import React, { useEffect, useMemo, useState } from "react";
import { useLazyGetRequirementListForSectionCBySGBPApplicationIdQuery, useLazyGetRequirementAdditionalInputFieldForSectionCBySGBPApplicationIdQuery, useLazyGetCriteriaRatingTickBySGBPProductInformationIdQuery, } from "../../../app/services/SelfEvaluationForm/selfEvalutionServiceApi";
import RequirementCard from "../utils/RequirementCard";
import SimpleAdditionalFieldsBlock from "../utils/SimpleAdditionalFieldsBlock";
import NoRecordFound from "../../../components/ui/noRecordFound/NoRecordFound";
import { useSelfEvaluationSection } from "../utils/useSectionEvaluation";

const SectionC = () => {
    const [tickRating, setTickRating] = useState(null);
    const [totalRating, setTotalRating] = useState(0);
    const [getCriteriaRatingTickBySGBPProductInformationId, { isLoading: tickRatingLoading, isSuccess: tickRatingSuccess, data: tickRatingData },] = useLazyGetCriteriaRatingTickBySGBPProductInformationIdQuery();

    const {
        state,
        dispatch,
        isLocked,
        sectionRecord,
        fieldList,
        handleFileUpload,
        handleDownload,
        handleDelete,
        handleCaseOfcComment,
        handleFieldChange,
        handleFieldComment,
        handleFieldBlur,
        isFieldInvalid,
        handleApprove,
        handleReject,
        handleClarificationSend,
        approveLoading,
        rejectLoading,
        clarificationLoading,
        rawRequirementRows,
        isFieldRequired,
        approvingReqId,
        rejectingReqId,
        clarifyingReqId,
    } = useSelfEvaluationSection("C", {
        useListQuery: useLazyGetRequirementListForSectionCBySGBPApplicationIdQuery,
        useFieldDefsQuery: useLazyGetRequirementAdditionalInputFieldForSectionCBySGBPApplicationIdQuery,
        onApplicationId: (applicationId) => getCriteriaRatingTickBySGBPProductInformationId(applicationId),
    });

    useEffect(() => {
        if (tickRatingSuccess && tickRatingData) {
            setTickRating(tickRatingData);
        }
    }, [tickRatingLoading, tickRatingSuccess, tickRatingData]);

    useEffect(() => {
        const count = Array.isArray(sectionRecord) ? sectionRecord.length : 0;
        setTotalRating(count);
    }, [sectionRecord]);

    const submittedRequirementCount = useMemo(() => {
        const requirements = state.sections?.C?.requirements || {};
        const requirementIds = rawRequirementRows
            ? rawRequirementRows.filter((item) => item.section === "C").map((item) => item.requirementId)
            : sectionRecord?.map((item) => item.sgbpRequirementId);
        return Object.entries(requirements).filter(
            ([key, req]) => requirementIds.includes(Number(key)) && req.files?.length > 0
        ).length;
    }, [state.sections?.C?.requirements, rawRequirementRows, sectionRecord]);

    const sectionCMinRating = useMemo(() => {
        if (!tickRating || submittedRequirementCount === 0) return null;
        const map = {
            1: tickRating.rating1Tick,
            2: tickRating.rating2Tick,
            3: tickRating.rating3Tick,
            4: tickRating.rating4Tick,
        };
        const entries = Object.entries(map);
        const minEntry = entries.reduce((min, current) => (current[1] < min[1] ? current : min));
        const matches = entries
            .filter(([, tickValue]) => tickValue <= submittedRequirementCount)
            .sort((a, b) => b[1] - a[1]);
        const match1 = matches.length > 0 ? matches[0] : minEntry;
        return Number(match1[0].replace("Tick", ""));
    }, [tickRating, submittedRequirementCount]);

    useEffect(() => {
        dispatch({ type: "SET_SECTION_C_COUNT", value: sectionCMinRating });
    }, [dispatch, sectionCMinRating]);

    useEffect(() => {
        dispatch({ type: "SET_SECTION_C_FILEUPLOAD", value: submittedRequirementCount });
    }, [dispatch, submittedRequirementCount]);

    return (
        <div className="section-card">
            <h3 className="section-title">Section C</h3>

            <div className="rating-box">
                <h4>Section C Additional Information</h4>
                <table className="rating-table">
                    <tbody>
                        {[1, 2, 3, 4].map((tick) => (
                            <tr key={tick}>
                                <td>{tick} Tick</td>
                                <td>
                                    <span>
                                        <input disabled className="change-value" value={tickRating?.[`rating${tick}Tick`] || 0} />
                                        / {totalRating}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {sectionRecord.length > 0 ? (
                <div className="evaluation-table-wrapper">
                    {sectionRecord.map((item) => {
                        const reqId = item.sgbpRequirementId;
                        const reqData = state?.sections?.C?.requirements?.[reqId] || { files: [], comment: "" };
                        const matchedFields = fieldList.filter((f) => f.sgbpRequirementId === reqId);

                        return (
                            <RequirementCard
                                key={reqId}
                                item={item}
                                reqId={reqId}
                                isLocked={isLocked}
                                reqData={reqData}
                                statusValue={state.sections?.C?.requirements?.[reqId]?.status}
                                caseOfficerComments={state.sections?.C?.requirements?.[reqId]?.caseOfficerComments}
                                peerReviewerComments={state.sections?.C?.requirements?.[reqId]?.peerReviewerComments}
                                onFileUpload={handleFileUpload}
                                onDownload={handleDownload}
                                onDelete={handleDelete}
                                onCaseOfcCommentChange={handleCaseOfcComment}
                                onClarificationSend={handleClarificationSend}
                                onReject={handleReject}
                                onApprove={handleApprove}
                                // clarificationLoading={clarificationLoading}
                                // rejectLoading={rejectLoading}
                                // approveLoading={approveLoading}
                                isReturnedApplication={state?.sections?.C?.isApplicationReturned}
                                clarificationLoading={clarificationLoading && clarifyingReqId === reqId}
                                rejectLoading={rejectLoading && rejectingReqId === reqId}
                                approveLoading={approveLoading && approvingReqId === reqId}
                            >
                                <SimpleAdditionalFieldsBlock
                                    matchedFields={matchedFields}
                                    sectionFields={state.sections?.C?.fields}
                                    isLocked={isLocked}
                                    isFieldInvalid={isFieldInvalid}
                                    onFieldChange={handleFieldChange}
                                    onFieldComment={handleFieldComment}
                                    onFieldBlur={handleFieldBlur}
                                    isFieldRequired={isFieldRequired}
                                />
                            </RequirementCard>
                        );
                    })}
                </div>
            ) : (
                <NoRecordFound />
            )}
        </div>
    );
};

export default SectionC;